import * as React from 'react';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { withHistoryContext } from 'Containers/FormContextHandler';
import type { IHistoryContext } from 'Containers/FormContextHandler';
import { isPartialDate } from 'neuro-utils';
import { filterDocumentsByType } from 'Utility/documentHandling';
import type { TreatmentDocumentType } from '../../definitions';

const oxygenTherapyAvailable = (documents: Array<TDocumentsWithEnddates>): boolean => {
  const activeDocs = filterDocumentsByType(documents, ['oxygenTherapy']).filter(d => !isPartialDate(d.endDate));
  if (
    activeDocs.some(
      d =>
        d.oxygenTherapyType &&
        ['oxygenConcentratorTherapeutic', 'oxygenConcentratorPalliative'].includes(d.oxygenTherapyType),
    ) &&
    activeDocs.some(d => d.oxygenTherapyType === 'stressOxygen')
  ) {
    return false;
  }
  return true;
};

const DEFAULT_DOCUMENTS: Array<TDocumentsWithEnddates> = [];

const DocumentCreationButtonGenerator = ({
  name,
  text,
  documents = DEFAULT_DOCUMENTS,
  startEdit,
  additionalData,
  fm,
}: { name: TDocTypesWithEnddates; text: string } & IHistoryContext<
  TDocumentsWithEnddates,
  IAdditionalData
>): React.JSX.Element => {
  const thisDocTypeDocuments = filterDocumentsByType(documents, [name]);

  const notEndedTreatment = thisDocTypeDocuments.find(p => {
    if (p._type === 'madTherapy') {
      return !p.isTreatmentStarted ? false : !isPartialDate(p.endDate);
    } else return !isPartialDate(p.endDate);
  });

  // Retrospective visit reason
  const retro = additionalData?.visitReasonRetro;

  const disabled = !retro && (name === 'oxygenTherapy' ? !oxygenTherapyAvailable(documents) : !!notEndedTreatment);

  return (
    <DocumentCreationButton
      name={name}
      text={text}
      onClick={startEdit && startEdit({}, name)}
      width={15}
      height={3}
      fontSize={14}
      alternate={true}
      disabled={disabled}
      disabledTooltip={<>{fm('treatment.endOngoingToAddNew')}</>}
    />
  );
};

type TDocTypesWithEnddates = SafeExtract<
  TreatmentDocumentType,
  'oxygenTherapy' | 'hfncTherapy' | 'papTherapy' | 'madTherapy' | 'patientDoesNotWantRespiratorySupportTherapy'
>;

type TDocumentsWithEnddates = IDocumentWithTypeV2<TDocTypesWithEnddates>;

interface IAdditionalData {
  visitReasonRetro?: boolean;
}

export const DocumentCreationButtonWithContext = withHistoryContext(DocumentCreationButtonGenerator);
